// NativePushInit — push в приложении (Capacitor); монтируется в __root рядом с PushInit.
//
// Два пути, оба после входа и онбординга:
//   1. Разрешение уже выдано → тихо регистрируем токен устройства (каждый запуск:
//      токен FCM ротируется, а привязка к аккаунту могла смениться).
//   2. Разрешение ещё не спрашивали → один раз показываем ту же подводку, что и в
//      браузере, и только по «Разрешить» зовём системный запрос (на iOS он
//      одноразовый — тратить его без подготовки нельзя).
// Токен уходит в `pushTokens.registerToken` с заявкой единого Firebase-проекта
// (см. apps/mobile/src/init-push.ts); отзывает его выход из аккаунта (lib/logout.ts).
// Нативная сборка на прежнем проекте (веб-часть доехала по OTA) разрешения не
// спрашивает и ничего не регистрирует: ей показывается «обновите приложение»
// (lib/push-app-update.ts).

import { useCallback, useEffect, useRef, useState } from "react";
import { PushPermissionPrompt } from "@/components/notifications/push-permission-prompt";
import {
	markPushPromptSeen,
	readPushPromptSeen,
} from "@/components/notifications/push-prompt-seen";
import {
	enableNativePush,
	getNativePushPermission,
	handleOutdatedNativePush,
} from "@/lib/native-push";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { api } from "@/lib/trpc-client";
import { useAuth } from "@/providers/auth-provider";

export function NativePushInit() {
	const {
		isAuthenticated,
		isLoading,
		isSessionPending,
		isOffline,
		pendingConsent,
		pendingOnboarding,
	} = useAuth();
	const registerMutation = api.pushTokens.registerToken.useMutation();
	const registerRef = useRef(registerMutation.mutateAsync);
	registerRef.current = registerMutation.mutateAsync;
	const [promptOpen, setPromptOpen] = useState(false);

	const ready =
		IS_CAPACITOR &&
		isAuthenticated &&
		!isLoading &&
		!isSessionPending &&
		!isOffline &&
		!pendingConsent &&
		!pendingOnboarding;

	const register = useCallback(
		(requestPermission: boolean) =>
			enableNativePush((input) => registerRef.current(input), { requestPermission }),
		[],
	);

	useEffect(() => {
		if (!ready) return;
		let cancelled = false;
		void (async () => {
			const state = await getNativePushPermission();
			if (cancelled || state === null || state === "denied") return;
			// Старая нативная сборка: ни разрешения, ни токена — только «обновите приложение».
			if (await handleOutdatedNativePush(state)) return;
			if (cancelled) return;
			if (state === "granted") {
				void register(false);
				return;
			}
			if (!readPushPromptSeen()) setPromptOpen(true);
		})();
		return () => {
			cancelled = true;
		};
	}, [ready, register]);

	const handleAllow = useCallback(() => {
		setPromptOpen(false);
		markPushPromptSeen();
		void register(true);
	}, [register]);

	const handleLater = useCallback(() => {
		setPromptOpen(false);
		markPushPromptSeen();
	}, []);

	if (!promptOpen) return null;

	return (
		<PushPermissionPrompt
			open={promptOpen}
			onOpenChange={(open) => {
				if (!open) handleLater();
			}}
			onAllow={handleAllow}
			onLater={handleLater}
		/>
	);
}
